import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import Pill from 'components/Luxkit/Pill'
import { pluralizeToString } from 'lib/pluralize'
import config from 'constants/config'
import FormatCurrency from 'components/Common/FormatCurrency'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'components/Luxkit/TextLink'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import moment from 'moment'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import InlineGroup from 'components/Common/InlineGroup'
import Heading from 'components/Luxkit/Typography/Heading'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import GeoContext from 'contexts/geoContext'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import TextButton from 'components/Luxkit/Button/TextButton'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import getBedbankPromoExtraValue from 'lib/bedbank/bedbankPromoExtraValue'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import Group from 'components/utils/Group'

const StyledPill = styled(Pill)`
  margin-left: ${rem(4)};
`

const PriceDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${rem(4)};
`

const TaxAndFeesWrapper = styled.div`
  display: flex;
  align-items: center;
`

const FlightPrice = styled(PriceDetails)`
  margin-top: ${rem(8)};
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  text-align: center;
`

interface Props {
  rate?: App.BedbankRate;
  rooms: Array<App.Occupants>;
  offer: App.BedbankOffer;
  flightOrigin?: App.Airport;
  flightPrice?: number;
  duration: number;
  checkIn?: moment.Moment;
  departureTooEarly?: boolean;
  flightsEnabled: boolean;
  onViewPackageOptions?: () => void;
}

function BedbankSummaryPriceDetails(props: Props) {
  const {
    rate,
    rooms,
    offer,
    checkIn,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    duration,
    onViewPackageOptions,
  } = props

  const { showTaxExclusivePrices } = useContext(GeoContext)
  const offerPageDispatch = useOfferPageDispatch()
  const hasFlights = !!flightPrice

  const sell = useBedbankOfferSell(offer, duration)

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterTypes })
  }, [offerPageDispatch])

  const taxesAndFees = rate?.totals?.taxesAndFees ?? 0
  const propertyFees = rate?.totals?.propertyFees ?? 0
  const hotelPrice = rate ? showTaxExclusivePrices ? rate.totals.exclusive : rate.totals.inclusive : sell?.price
  let hotelValue: undefined | number
  let hotelDiscount: number

  if (rate) {
    const pkg = offer.packages.find((pkg) => pkg.roomId === rate.roomId)
    const isPromoEnabledRate = offer.promotions.some((promo) => duration >= promo.los && promo.restrictToRates.some((rateId) => rateId === rate.id))

    if (isPromoEnabledRate) {
      const inclusionsAmount = pkg.sell ? getBedbankPromoExtraValue({
        price: pkg.sell.price,
        value: pkg.sell.value,
        minLos: pkg.sell.los,
        duration,
      }) : 0
      hotelValue = inclusionsAmount + (showTaxExclusivePrices ? rate.totals.exclusive : rate.totals.inclusive)
      hotelDiscount = Number((inclusionsAmount / hotelValue).toFixed(2))
    } else {
      hotelValue = rate.value
      hotelDiscount = rate.discount
    }
  } else {
    hotelValue = sell?.value
    hotelDiscount = sell?.discount
  }

  const hotelNights = rate?.nights ?? sell?.los
  const exclusivePriceColor = useHotelPriceColour(taxesAndFees)
  const totalPrice = showTaxExclusivePrices ? hotelPrice : hotelPrice + propertyFees

  const departureTooFarInFuture = useMemo(() => checkIn?.diff(moment.utc(), 'days') > 330, [checkIn])

  return (
    <Group direction="vertical" horizontalAlign="center">
      <Group direction="vertical" horizontalAlign="center" gap={4}>
        <Group direction="vertical" horizontalAlign="center" gap={4}>
          <Group direction="vertical" horizontalAlign="center">
            <Caption variant="medium">{pluralizeToString('night', hotelNights)} from</Caption>
            <InlineGroup vertical="center">
              <Heading variant="heading3" as="span" colour={exclusivePriceColor}>
                <FormatCurrency format="roundedDollar" value={totalPrice} />
              </Heading>
              <BodyText as="span" variant="large" weight="semi-bold">/total</BodyText>
            </InlineGroup>
          </Group>
          {config.businessTraveller.currentAccountMode === 'business' &&
            <BusinessTravellerOfferCreditsTextLink
        type="exact"
        offer={offer}
        totalCost={totalPrice}
        numberOfNights={hotelNights}
      />}
        </Group>
        {taxesAndFees > 0 && (
          <TaxAndFeesWrapper>
            <TaxesAndFees taxAmount={taxesAndFees} />
        &nbsp;
            <PriceDetailsModal
          offerPage
          offer={offer}
          rooms={rooms}
          taxesAndFees={taxesAndFees}
          propertyFees={propertyFees}
          hotelPrice={hotelPrice}
          duration={hotelNights}
          label="infoButton"
          labelSize="large"
          cta={
            <TextButton kind="primary" fit="flex">
              View package options
            </TextButton>
          }
          onCtaAfterClose={onViewPackageOptions}
        />
          </TaxAndFeesWrapper>
        )}
        {hotelDiscount > 0 &&
          <ValueWrapper variant="medium" weight="bold">
            <Caption variant="large" weight="bold">
              Valued up to <FormatCurrency format="roundedDollar" value={hotelValue} />
              {hotelDiscount > config.displayDiscountThreshold.bedbank &&
                <StyledPill kind="primary" variant="success">Save up to {Math.round(hotelDiscount * 100)}%</StyledPill>
            }
            </Caption>
          </ValueWrapper>
      }
        {flightsEnabled &&
          <FlightPrice>
            {!hasFlights && flightOrigin && flightOrigin.code === offer.flights?.airportCode &&
              <ChangeDepartureWrapper>
                <Caption variant="large" colour="neutral-three">
                  No flights available from {flightOrigin.name}. Please select another airport.
                </Caption>
                <TextLink
                onClick={onToggleFilter}
                data-filter="airport"
              >
                  Change departure airport
                </TextLink>
              </ChangeDepartureWrapper>
          }
            {!hasFlights && flightOrigin?.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly &&
              <Caption variant="large" colour="neutral-three" align="center">
                Add flights to the hotel package. Select from all major airlines.
              </Caption>
          }
            {!hasFlights && departureTooFarInFuture &&
              <Caption variant="large" colour="neutral-three">
                Flights can only be booked up to 11 months in advance.
              </Caption>
          }
            {flightOrigin?.code !== offer.flights?.airportCode && departureTooEarly &&
              <ChangeDepartureWrapper>
                <BodyText variant="small" colour="neutral-two">
                  Flights cannot be booked online when check-in is within 24 hours
                </BodyText>
                <TextLink
                onClick={onToggleFilter}
                data-filter="datepicker"
              >
                  Change departure date
                </TextLink>
              </ChangeDepartureWrapper>
          }
            {hasFlights && !departureTooEarly &&
              <InlineGroup vertical="center">
                {/* TODO: When translating this, use the HotelPlusFlightsFrom component here */}
                <LinePlaneFlyIcon />&nbsp;
                <Caption variant="medium">Hotel + flights from&nbsp;</Caption>
                <Caption variant="medium" colour={exclusivePriceColor} weight="bold">
                  <FormatCurrency value={totalPrice + flightPrice} />/package*&nbsp;
                </Caption>
                {!rooms && <Caption variant="medium" colour="neutral-three">
                  (Two Adults)
                </Caption>}
              </InlineGroup>
          }
            <AtolProtectedTooltip />
          </FlightPrice>
      }
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((state) => ({
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
}))(BedbankSummaryPriceDetails)
